import type { WaitForReplyNodeData, WaitNodeData, WaitUnit, WaitUntilNodeData } from 'app/types/workflows'
import { fieldSource } from 'app/lib/workflows/dataContext'
import { type Panel, useConditionValueOptions } from './panelShared'
import { ChoiceSelect, Field, TextField, ToggleField } from './fields'
import { ConditionEditor } from './ConditionEditor'
import { Section } from './Section'
import { DURATION_INPUT, waitUnitChoices } from './panelShared'

export function WaitPanel({ data, set, errors }: Panel<WaitNodeData>) {
  return (
    <>
      <Section>
        <Field label="Duration" error={errors.duration}>
          <div className="flex items-center gap-2">
            <div className="w-16">
              <TextField
                type="number"
                min={1}
                invalid={!!errors.duration}
                className={DURATION_INPUT}
                value={data.duration}
                onChange={(e) => set({ duration: Math.max(1, Number(e.target.value) || 1) })}
              />
            </div>
            <ChoiceSelect
              className="w-28"
              value={data.unit}
              choices={waitUnitChoices(data.duration)}
              onChange={(v) => set({ unit: v as WaitUnit })}
            />
          </div>
        </Field>
        <ToggleField
          label="Business hours only"
          info="When the wait ends outside business hours, the next step holds until they start."
          checked={data.businessHoursOnly}
          onChange={(checked) => set({ businessHoursOnly: checked })}
        />
      </Section>
    </>
  )
}

export function WaitUntilPanel({
  data,
  set,
  errors,
  availableFields,
}: Panel<WaitUntilNodeData> & { availableFields: string[] }) {
  // The engine judges these against the run's contact only (TRK-6036).
  const contactFields = availableFields.filter((key) => fieldSource(key) === `contact`)
  const valueOptions = useConditionValueOptions(contactFields)
  return (
    <>
      <Section title="Met when the contact">
        <ConditionEditor
          match={data.match}
          conditions={data.conditions}
          onChange={set}
          availableFields={contactFields}
          valueOptions={valueOptions}
          error={errors.conditions}
          label={null}
          events={false}
        />
        <div className="text-[11px] text-subtle">
          Checked when the wait starts, whenever the contact changes, and once a day. The first match takes the Met
          path.
        </div>
      </Section>
      <Section title="Time limit">
        <Field label="Timed out after" error={errors.duration}>
          <div className="flex items-center gap-2">
            <div className="w-16">
              <TextField
                type="number"
                min={1}
                invalid={!!errors.duration}
                className={DURATION_INPUT}
                value={data.duration}
                onChange={(e) => set({ duration: Math.max(1, Number(e.target.value) || 1) })}
              />
            </div>
            <ChoiceSelect
              className="w-28"
              value={data.unit}
              choices={waitUnitChoices(data.duration)}
              onChange={(v) => set({ unit: v as WaitUnit })}
            />
          </div>
        </Field>
      </Section>
    </>
  )
}

export function WaitForReplyPanel({ data, set }: Panel<WaitForReplyNodeData>) {
  const hasDeadline = data.within !== undefined
  return (
    <>
      <Section>
        <ToggleField
          label="Reply deadline"
          info="Waits for the contact to reply to the text above. Replied takes one path; past the deadline, No reply takes the other. Off waits with no time limit. Place this after a Send SMS."
          checked={hasDeadline}
          onChange={(on) => set(on ? { within: data.within ?? 1, unit: data.unit ?? `days` } : { within: undefined })}
        />
        {hasDeadline && (
          <Field label="No reply after">
            <div className="flex items-center gap-2">
              <div className="w-16">
                <TextField
                  type="number"
                  min={1}
                  className={DURATION_INPUT}
                  value={data.within ?? 1}
                  onChange={(e) => set({ within: Math.max(1, Number(e.target.value) || 1) })}
                />
              </div>
              <ChoiceSelect
                className="w-28"
                value={data.unit ?? `days`}
                choices={waitUnitChoices(data.within ?? 1)}
                onChange={(v) => set({ unit: v as WaitUnit })}
              />
            </div>
          </Field>
        )}
      </Section>
      <Section title="If someone else steps in">
        <ToggleField
          label="Stop if a person takes over"
          hint="Ends the workflow, without sending the no-reply message, when someone takes the thread off the AI agent, a colleague replies by hand, or a later workflow takes over the conversation. A patient who opts out always stops it."
          checked={data.stopOnHumanTakeover !== false}
          onChange={(on) => set({ stopOnHumanTakeover: on ? undefined : false })}
        />
      </Section>
    </>
  )
}

export function WaitForCallResultPanel() {
  return (
    <Section>
      <div className="text-xs text-subtle">
        Waits for the call above to finish, then forks on Answered, No answer, or Voicemail.
      </div>
    </Section>
  )
}
